import { FC } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { AvailablePaymentCurrency, formatSmartNarrowCurrencyFromCents } from "@simplyk/common";
import { DateTime } from "luxon";

import { AuctionBidBadgeChip } from "./AuctionBidBadgeChip";
import type { AuctionBidBadge } from "./AuctionBidBadgeChip";
import { AuctionRateCardLayout } from "./AuctionRateCardLayout";

import { mergeSx } from "@/common/helpers/mergeSx";
import { unstyledButtonSx } from "@/common/helpers/unstyledButtonSx";
import { ResponsiveMultipleImageDisplay } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/MultipleImageDisplay/ResponsiveMultipleImageDisplay";
import { RateCardDescription } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCardDescription";
import { RateCardPrice } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCardPrice";
import { RateCardTaxCaption } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCardTaxCaption";
import { RateCardTitle } from "@/canvas/features/FormV2/blocks/selection/ticketing-selection/Rates/RateCard/RateCardTitle";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormChip } from "@/forms-design-system/FormChip";
import { FormDivider } from "@/forms-design-system/FormDivider";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTooltip } from "@/forms-design-system/FormTooltip";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ChevronRight, Flame, Flash, Success } from "@/icons/outlined";

export interface AuctionRateCardProps {
  id: string;
  title: string;
  description?: string;
  photoUrls?: string[];
  price: number | null | undefined;
  formCurrency: AvailablePaymentCurrency;
  topBidderName?: string;
  hasBids?: boolean;
  bidBadge?: AuctionBidBadge;
  userBidAmountInCents?: number;
  isHotItem?: boolean;
  isAutoBidBadge?: boolean;
  hasServerAutoBid?: boolean;
  autoBidMaxAmountInCents?: number;
  isSoldOut?: boolean;
  ticketingIsClosed?: boolean;
  isRateClosed?: boolean;
  salesOpenDate?: Date | null;
  eventTimezone?: string | null;
  hasBrandBorder?: boolean;
  isLocallyOutbid?: boolean;
  antiSnipingInfo?: React.ReactNode;
  showTaxMention?: boolean;
  onBidClick?: () => void;
  onDetailsClick?: () => void;
}

export const AuctionRateCard: FC<AuctionRateCardProps> = ({
  id,
  title,
  description,
  photoUrls,
  price,
  formCurrency,
  topBidderName,
  hasBids = false,
  bidBadge,
  userBidAmountInCents,
  isHotItem = false,
  isAutoBidBadge = false,
  hasServerAutoBid = false,
  autoBidMaxAmountInCents,
  isSoldOut = false,
  ticketingIsClosed = false,
  isRateClosed = false,
  salesOpenDate,
  eventTimezone,
  hasBrandBorder = false,
  isLocallyOutbid = false,
  antiSnipingInfo,
  showTaxMention = false,
  onBidClick,
  onDetailsClick,
}) => {
  const { t } = useTranslate();
  const { isoLocale, locale } = useLocaleContext();
  const hasImages = Boolean(photoUrls && photoUrls.length > 0);
  const hideDescription = Boolean(bidBadge);
  const hasBidSet = userBidAmountInCents !== undefined;
  const isActive = hasBrandBorder || hasBidSet;

  const isSalesScheduled = Boolean(salesOpenDate && salesOpenDate > new Date());
  const zonedOpenDate =
    isSalesScheduled && salesOpenDate
      ? DateTime.fromJSDate(salesOpenDate)
          .setZone(eventTimezone ?? undefined)
          .setLocale(locale)
      : null;
  const openingTooltipText = zonedOpenDate
    ? t("ticketing", "bidsOpeningOn", {
        date: zonedOpenDate.toLocaleString(DateTime.DATE_MED),
        time: zonedOpenDate.toLocaleString(DateTime.TIME_SIMPLE),
      })
    : "";

  const bidButton = (
    <FormButton
      variant="tonal"
      onClick={(e) => {
        e.stopPropagation();
        if (!isSalesScheduled) {
          onBidClick?.();
        }
      }}
      sx={{ minWidth: 140 }}
      disabled={isSoldOut || ticketingIsClosed || isRateClosed || isSalesScheduled}
      data-test="auction-bid-button"
    >
      {isSalesScheduled ? t("ticketing", "openingSoon") : t("ticketing", "auctionV2.bid")}
    </FormButton>
  );
  const priceCaption = topBidderName ? (
    <FormTypography variant="body2" vibe="brand">
      {t("ticketing", "auctionV2.currentBidBy", { name: topBidderName })}
    </FormTypography>
  ) : (
    <FormTypography variant="body2" vibe="supershy">
      {t("ticketing", hasBids ? "auctionV2.input.currentBid" : "auctionV2.startingBid")}
    </FormTypography>
  );

  const bidActionNode = isSalesScheduled ? (
    <FormTooltip title={openingTooltipText}>
      <span style={{ display: "inline-block" }}>{bidButton}</span>
    </FormTooltip>
  ) : (
    bidButton
  );

  return (
    <AuctionRateCardLayout
      id={id}
      isSoldOut={isSoldOut}
      hasBrandBorder={isActive}
      onClick={onDetailsClick}
      slots={{
        image: hasImages ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <Box onClick={(e) => e.stopPropagation()}>
            <ResponsiveMultipleImageDisplay purchasedNumber={0} photoUrls={photoUrls ?? []} title={title} />
          </Box>
        ) : undefined,

        bidBadge: bidBadge ? <AuctionBidBadgeChip bidBadge={bidBadge} /> : undefined,

        title: <RateCardTitle>{title}</RateCardTitle>,

        detailsChevron: onDetailsClick ? (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <FormIcon
            size="medium"
            color="currentColor"
            aria-label={`${t("ticketing", "auctionV2.details")}: ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              onDetailsClick();
            }}
            sx={(theme) => ({ flexShrink: 0, color: theme.palette.forms?.text.moderate })}
          >
            <ChevronRight />
          </FormIcon>
        ) : undefined,

        price: <RateCardPrice price={price} formCurrency={formCurrency} />,

        priceCaption: showTaxMention ? (
          <Stack direction="row" alignItems="center" gap={1}>
            <RateCardTaxCaption />
            <FormDivider orientation="vertical" flexItem />
            {priceCaption}
          </Stack>
        ) : (
          priceCaption
        ),

        hotItemBadge: isHotItem ? (
          <FormChip label={t("ticketing", "auctionV2.hotItem")} icon={<Flame />} vibe="emphasis" variant="outlined" />
        ) : undefined,

        callToAction: hasBidSet ? (
          <FormChip
            label={t("ticketing", isAutoBidBadge ? "auctionV2.autoBidSet" : "auctionV2.bidSet", {
              amount: formatSmartNarrowCurrencyFromCents(userBidAmountInCents, {
                locale: isoLocale,
                currency: formCurrency,
              }),
            })}
            icon={isAutoBidBadge ? <Flash /> : <Success />}
            vibe="positive"
            variant="outlined"
            sx={(theme) => ({
              height: 36,
              borderRadius: 1,
              paddingInline: 2,
              ...theme.typography.subtitle2,
            })}
            components={{ icon: { size: "small" } }}
          />
        ) : (
          bidActionNode
        ),

        outbidHelper: isLocallyOutbid ? (
          <FormTypography variant="body2" role="status" sx={(theme) => ({ color: theme.palette.forms?.text.error })}>
            {t("ticketing", "auctionV2.bidBlock.alreadyOutbidHelper")}
          </FormTypography>
        ) : undefined,

        antiSnipingInfo,

        detailsLink: onDetailsClick ? (
          <Stack
            component="button"
            type="button"
            aria-label={`${t("ticketing", "auctionV2.details")}: ${title}`}
            direction="row"
            gap={1}
            alignItems="center"
            sx={mergeSx(unstyledButtonSx, (theme) => ({
              borderRadius: theme.radius(1),
              outlineColor: "transparent",
              ["&:focus-visible"]: {
                outline: `3px solid ${theme.palette.formsComponents?.global.focusOutlineColor}`,
              },
            }))}
            onClick={(e) => {
              e.stopPropagation();
              onDetailsClick();
            }}
          >
            <FormTypography variant="body2" vibe="supershy" component="span">
              {t("ticketing", "auctionV2.details")}
            </FormTypography>
            <FormIcon
              size="xsmall"
              color="currentColor"
              sx={(theme) => ({ color: theme.palette.forms?.text.supershy })}
            >
              <ChevronRight />
            </FormIcon>
          </Stack>
        ) : undefined,

        description:
          !hideDescription && description ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <Box onClick={(e) => e.stopPropagation()}>
              <RateCardDescription active={isActive} description={description} />
            </Box>
          ) : undefined,

        autoBidLabel:
          autoBidMaxAmountInCents !== undefined ? (
            <Stack direction="row" gap={0.5} alignItems="center">
              <FormIcon size="xsmall" color="currentColor" sx={(theme) => ({ color: theme.palette.forms?.text.quiet })}>
                <Flash />
              </FormIcon>
              <FormTypography variant="body2" vibe="quiet">
                {t("ticketing", "auctionV2.autoBidUpTo", {
                  amount: formatSmartNarrowCurrencyFromCents(autoBidMaxAmountInCents, {
                    locale: isoLocale,
                    currency: formCurrency,
                  }),
                })}
              </FormTypography>
            </Stack>
          ) : hasServerAutoBid ? (
            <Stack direction="row" gap={0.5} alignItems="center">
              <FormIcon size="xsmall" color="currentColor" sx={(theme) => ({ color: theme.palette.forms?.text.quiet })}>
                <Flash />
              </FormIcon>
              <FormTypography variant="body2" vibe="quiet">
                {t("ticketing", "auctionV2.autoBidActive")}
              </FormTypography>
            </Stack>
          ) : undefined,
      }}
    />
  );
};
